import { useEffect, useMemo, useState } from 'react';
import {
  Boxes,
  Calculator,
  Check,
  Copy,
  Edit3,
  MoreHorizontal,
  PackageOpen,
  Plus,
  Search,
  ShieldCheck,
  Tag,
  Trash2,
} from 'lucide-react';
import { collection, deleteDoc, doc, onSnapshot, serverTimestamp, setDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import {
  catalogIdentityFields,
  findCatalogDuplicate,
  matchesCatalogSearch,
  normalizeCatalogValue,
  uniqueCatalogCopyName,
} from '../lib/catalogProduct';
import { calculateEstimate } from '../lib/pricing';
import ConfirmDialog from './ConfirmDialog';
import ProductForm from './ProductForm';

const currency = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

function timestampValue(value) {
  if (!value) return 0;
  if (typeof value.toMillis === 'function') return value.toMillis();
  return Number(value) || 0;
}

function readableError(error) {
  const code = error?.code || '';
  if (code.includes('permission-denied')) return 'Firestore blocked your product catalog. Confirm the published rules allow your user subcollections.';
  if (code.includes('unavailable')) return 'MM could not reach Firebase. Check your connection and try again.';
  return error?.message || 'The product catalog could not be updated.';
}

export default function Catalog({ user, onRequestAuth, onNavigate, onPriceProduct }) {
  const [products, setProducts] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(Boolean(user));
  const [query, setQuery] = useState('');
  const [error, setError] = useState('');
  const [editor, setEditor] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [openMenu, setOpenMenu] = useState(null);
  const [working, setWorking] = useState(false);
  const [toast, setToast] = useState('');

  useEffect(() => {
    if (!user || !db) {
      setProducts([]);
      setProfiles([]);
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    const stopProducts = onSnapshot(collection(db, 'users', user.uid, 'products'), (snapshot) => {
      setProducts(snapshot.docs.map((item) => ({ id: item.id, ...item.data() })).sort((a, b) => timestampValue(b.updatedAt) - timestampValue(a.updatedAt)));
      setLoading(false);
    }, (snapshotError) => { setError(readableError(snapshotError)); setLoading(false); });
    const stopProfiles = onSnapshot(collection(db, 'users', user.uid, 'costProfiles'), (snapshot) => {
      setProfiles(snapshot.docs.map((item) => ({ id: item.id, ...item.data() })).sort((a, b) => Number(b.isDefault) - Number(a.isDefault)));
    }, (snapshotError) => setError(readableError(snapshotError)));
    return () => { stopProducts(); stopProfiles(); };
  }, [user]);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = window.setTimeout(() => setToast(''), 2600);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const visibleProducts = useMemo(() => {
    return products.filter((product) => matchesCatalogSearch(product, query));
  }, [products, query]);

  const summaries = useMemo(() => products.map((product) => {
    const profile = profiles.find((item) => item.id === product.profileId) || null;
    return { product, profile, results: calculateEstimate(profile, product) };
  }), [products, profiles]);
  const averagePrice = summaries.length ? summaries.reduce((sum, item) => sum + item.results.recommendedPricePerPiece, 0) / summaries.length : 0;
  const categoryCount = new Set(products.map((product) => product.category || 'Uncategorized')).size;

  const saveProduct = async (values) => {
    if (!user || !db) throw new Error('Sign in before saving a product.');
    const duplicate = findCatalogDuplicate(products, values, editor?.id);
    if (duplicate) {
      const duplicateIsSku = normalizeCatalogValue(values.sku)
        && normalizeCatalogValue(values.sku) === normalizeCatalogValue(duplicate.sku);
      throw new Error(duplicateIsSku
        ? `SKU ${values.sku.trim()} is already used by ${duplicate.name || 'another catalog product'}.`
        : `A catalog product named ${duplicate.name || values.name} already exists.`);
    }
    setWorking(true);
    try {
      const reference = editor?.id ? doc(db, 'users', user.uid, 'products', editor.id) : doc(collection(db, 'users', user.uid, 'products'));
      await setDoc(reference, {
        ...values,
        ...catalogIdentityFields(values),
        ownerId: user.uid,
        ...(editor?.id ? {} : { createdAt: serverTimestamp() }),
        updatedAt: serverTimestamp(),
      }, { merge: true });
      setEditor(null);
      setToast(editor?.id ? 'Catalog product updated.' : 'Catalog product created.');
    } catch (saveError) {
      throw new Error(readableError(saveError));
    } finally {
      setWorking(false);
    }
  };

  const duplicateProduct = async (product) => {
    if (!user || !db) return;
    setOpenMenu(null);
    setWorking(true);
    try {
      const copy = { ...product };
      delete copy.id;
      delete copy.createdAt;
      delete copy.updatedAt;
      delete copy.sourceEstimateId;
      delete copy.normalizedName;
      delete copy.normalizedSku;
      const copyName = uniqueCatalogCopyName(products, product.name);
      await setDoc(doc(collection(db, 'users', user.uid, 'products')), {
        ...copy,
        name: copyName,
        sku: '',
        ...catalogIdentityFields({ name: copyName, sku: '' }),
        ownerId: user.uid,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      setToast('Catalog product duplicated.');
    } catch (duplicateError) {
      setError(readableError(duplicateError));
    } finally {
      setWorking(false);
    }
  };

  const deleteProduct = async () => {
    if (!user || !db || !deleteTarget) return;
    setWorking(true);
    try {
      await deleteDoc(doc(db, 'users', user.uid, 'products', deleteTarget.id));
      setDeleteTarget(null);
      setToast('Catalog product deleted.');
    } catch (deleteError) {
      setError(readableError(deleteError));
    } finally {
      setWorking(false);
    }
  };

  if (!user) {
    return (
      <section className="signed-out-state brushed-panel">
        <span className="signed-out-state__icon"><PackageOpen size={30} /></span>
        <p className="eyebrow">Reusable product catalog</p>
        <h1>Sign in to build your product library.</h1>
        <p>Keep repeatable print assumptions private, reusable, and ready to send back into the live calculator.</p>
        <div className="signed-out-state__actions"><button className="button button--primary button--large" type="button" onClick={() => onRequestAuth('signup')}>Create account</button><button className="button button--ghost button--large" type="button" onClick={() => onRequestAuth('signin')}>Sign in</button></div>
      </section>
    );
  }

  return (
    <section className="profiles-page feature-page">
      <header className="profiles-page__hero">
        <div><p className="eyebrow">Product catalog · Live</p><h1>Price repeat work in one move.</h1><p>Save each product’s production assumptions once, then launch a fresh calculation whenever costs, quantities, or margins change.</p></div>
        <button className="button button--primary button--large" type="button" onClick={() => profiles.length ? setEditor({}) : onNavigate('cost-profiles')}><Plus size={19} /> {profiles.length ? 'New product' : 'Create cost profile first'}</button>
      </header>

      {error && <div className="config-banner config-banner--error" role="alert"><ShieldCheck size={18} /><span>{error}</span><button type="button" onClick={() => setError('')}>Dismiss</button></div>}

      <section className="profiles-summary">
        <article><span><PackageOpen size={19} /></span><div><small>Saved products</small><strong>{products.length}</strong></div></article>
        <article><span><Tag size={19} /></span><div><small>Categories</small><strong>{categoryCount}</strong></div></article>
        <article><span><Calculator size={19} /></span><div><small>Average item price</small><strong>{currency.format(averagePrice)}</strong></div></article>
        <article><span><Boxes size={19} /></span><div><small>Pricing profiles</small><strong>{profiles.length}</strong></div></article>
      </section>

      <section className="feature-toolbar brushed-panel">
        <label className="estimates-search"><Search size={17} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search products, SKUs, categories, or profiles" /></label>
        <span>{visibleProducts.length} of {products.length} product{products.length === 1 ? '' : 's'}</span>
      </section>

      <section className="feature-library brushed-panel">
        {loading ? (
          <div className="profiles-loading" aria-live="polite"><span /><span /><span /><p>Loading your catalog…</p></div>
        ) : products.length === 0 ? (
          <div className="profiles-empty"><span><PackageOpen size={28} /></span><h3>Your repeatable products will live here.</h3><p>Create a product from known slicer totals, then reprice it anytime without rebuilding the job.</p><button className="button button--primary" type="button" onClick={() => profiles.length ? setEditor({}) : onNavigate('cost-profiles')}><Plus size={18} /> {profiles.length ? 'Create first product' : 'Create cost profile first'}</button></div>
        ) : visibleProducts.length === 0 ? (
          <div className="profiles-empty"><span><Search size={28} /></span><h3>No products match that search.</h3><p>Try a product name, SKU, category, or cost profile.</p></div>
        ) : (
          <div className="feature-card-grid">
            {visibleProducts.map((product) => {
              const summary = summaries.find((item) => item.product.id === product.id);
              const results = summary?.results || calculateEstimate(null, product);
              return (
                <article className="feature-card product-card" key={product.id}>
                  <div className="feature-card__head">
                    <span className="feature-card__icon"><PackageOpen size={20} /></span>
                    <div><h2>{product.name || 'Untitled product'}</h2><p>{product.sku || product.category || 'Uncategorized'} · {product.profileName || 'Cost profile needed'}</p></div>
                    <div className="profile-card__menu-wrap">
                      <button className="icon-button profile-card__menu" type="button" onClick={() => setOpenMenu(openMenu === product.id ? null : product.id)} aria-label={`Actions for ${product.name || 'product'}`} aria-expanded={openMenu === product.id}><MoreHorizontal size={18} /></button>
                      {openMenu === product.id && <div className="profile-menu"><button type="button" onClick={() => { setEditor(product); setOpenMenu(null); }}><Edit3 size={16} /> Edit product</button><button type="button" onClick={() => duplicateProduct(product)} disabled={working}><Copy size={16} /> Duplicate</button><button className="profile-menu__danger" type="button" onClick={() => { setDeleteTarget(product); setOpenMenu(null); }}><Trash2 size={16} /> Delete</button></div>}
                    </div>
                  </div>
                  <div className="feature-card__price"><small>Recommended per piece</small><strong>{summary?.profile ? currency.format(results.recommendedPricePerPiece) : 'Needs profile'}</strong><span>{Number(product.targetMargin || 0).toFixed(1)}% margin · {Number(product.quantity || 1)} pcs</span></div>
                  <div className="feature-card__metrics"><span><small>Filament</small><strong>{Number(product.filamentGrams || 0).toFixed(1)} g</strong></span><span><small>Print time</small><strong>{Number(product.printHours || 0)}h {Number(product.printMinutes || 0)}m</strong></span><span><small>Batch profit</small><strong>{currency.format(results.totalProfit)}</strong></span></div>
                  <footer className="feature-card__actions"><button className="button button--ghost" type="button" onClick={() => setEditor(product)}><Edit3 size={15} /> Edit</button><button className="button button--primary" type="button" onClick={() => onPriceProduct(product)}><Calculator size={16} /> Price product</button></footer>
                </article>
              );
            })}
          </div>
        )}
      </section>

      {editor && <ProductForm product={editor.id ? editor : null} profiles={profiles} saving={working} onClose={() => !working && setEditor(null)} onSave={saveProduct} />}
      {deleteTarget && <ConfirmDialog title={`Delete ${deleteTarget.name || 'this product'}?`} message="This removes the reusable catalog entry. Saved estimates and cost profiles are not affected." working={working} onCancel={() => !working && setDeleteTarget(null)} onConfirm={deleteProduct} />}
      {toast && <div className="toast" role="status"><Check size={17} /> {toast}</div>}
    </section>
  );
}
